import type { ApiAvailabilityRule, ApiStock, AvailabilityRuleAttributes } from './types';

// The product editor still shows availability as active dates plus its Stock
// section, which predates availability rules and is really a list of blocked
// dates, not inventory. These map between that view and the product's rules:
// the lifecycle rule is the active dates, and each block rule that sets only
// dates is a quantity-0 row whose id is the rule's. Rules the editor can't show
// are left untouched by its writes.

export function lifecycleRule(rules: ApiAvailabilityRule[]): ApiAvailabilityRule | undefined {
  return rules.find((rule) => rule.source === 'lifecycle');
}

function isStockRule(rule: ApiAvailabilityRule): boolean {
  return (
    rule.effect === 'block' &&
    rule.days_of_week == null &&
    rule.start_time == null &&
    rule.fulfilment_types == null
  );
}

export function stockRowsFromRules(rules: ApiAvailabilityRule[]): ApiStock[] {
  return rules
    .filter(isStockRule)
    .sort((a, b) => a.id - b.id)
    .map((rule) => ({ id: rule.id, quantity: 0, start_date: rule.start_date, end_date: rule.end_date }));
}

export function lifecycleRuleAttributes(
  rules: ApiAvailabilityRule[],
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): AvailabilityRuleAttributes[] {
  const rule = lifecycleRule(rules);
  const start_date = startDate || null;
  const end_date = endDate || null;

  if (!start_date && !end_date) return rule ? [{ id: rule.id, _destroy: true }] : [];
  if (rule) return [{ id: rule.id, start_date, end_date }];
  return [{ effect: 'allow', source: 'lifecycle', start_date, end_date }];
}

// A row with stock on hand blocks nothing, so it removes its rule; a row at 0
// blocks its dates. Rows removed from the editor remove their rule.
export function stockRuleAttributes(
  rows: Array<{ id?: number; quantity: number | null; start_date: string | null; end_date: string | null }>,
  rules: ApiAvailabilityRule[],
): AvailabilityRuleAttributes[] {
  const attrs: AvailabilityRuleAttributes[] = [];

  for (const row of rows) {
    const inStock = row.quantity != null && row.quantity > 0;
    if (inStock) {
      if (row.id) attrs.push({ id: row.id, _destroy: true });
    } else if (row.id) {
      attrs.push({ id: row.id, start_date: row.start_date, end_date: row.end_date });
    } else {
      attrs.push({ effect: 'block', source: 'unavailable_dates', start_date: row.start_date, end_date: row.end_date });
    }
  }

  const currentIds = new Set(rows.map((row) => row.id).filter(Boolean));
  for (const stock of stockRowsFromRules(rules)) {
    if (!currentIds.has(stock.id)) attrs.push({ id: stock.id, _destroy: true });
  }

  return attrs;
}
